import { QueryClient, UseQueryOptions } from '@tanstack/react-query'
import { addDays, isAfter } from 'date-fns'
import { RouteData, useApiClient } from '../api-client-provider'
import { useTreatyQuery } from '../utils/use-treaty-query'

export type MyCoupon = RouteData<'/coupon/v2/my-coupons'>[number]

/** Prefix for every `useMyCoupons` query, including machine/shop-scoped lists. */
export const MY_COUPONS_QUERY_KEY = ['my-coupons'] as const

/** Drop cached coupon lists so remaining uses / newly granted coupons refetch. */
export function invalidateMyCoupons(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: MY_COUPONS_QUERY_KEY })
}

export type MyCouponsContext = {
  /** Narrow to coupons valid at this machine right now (implies its shop). */
  machineId?: number
  /** Narrow to coupons valid at this shop right now. */
  shopId?: number
  /** ISO timestamp to validate at instead of now, e.g. a reservation's booking time. */
  at?: string
}

/**
 * Coupons flagged "visible to users" that the logged-in user is eligible for.
 * Without a context this is the user's whole list; with a machine or shop it is
 * exactly what would apply there right now, so it is safe to offer as tap-to-fill
 * on a coupon entry screen. Callers gate `enabled` on the auth token and the
 * app's `myCoupons` feature flag.
 */
export function useMyCoupons(
  context: MyCouponsContext = {},
  options?: Omit<UseQueryOptions<MyCoupon[], Error>, 'queryFn' | 'queryKey'>,
) {
  const client = useApiClient()

  return useTreatyQuery({
    queryKey: [...MY_COUPONS_QUERY_KEY, context.machineId ?? null, context.shopId ?? null, context.at ?? null],
    staleTime: 30_000,
    retry: false,
    queryFn: () => client.coupon.v2['my-coupons'].get({ query: context }),
    ...(options as {}),
  })
}

export type CouponDiscount =
  | { kind: 'fixed'; amount: number; currency: string }
  | { kind: 'percentage'; value: number }

/** A coupon carries either a fixed amount (in its own currency, or the
 * caller's when the coupon is currency-agnostic) or a percentage. */
export function getCouponDiscount(coupon: MyCoupon, fallbackCurrency: string): CouponDiscount {
  return coupon.fixDiscount > 0
    ? { kind: 'fixed', amount: coupon.fixDiscount, currency: coupon.currency ?? fallbackCurrency }
    : { kind: 'percentage', value: coupon.percentage }
}

/** "HH:MM:SS" from postgres → "HH:MM"; null stays null. */
export function toShortTime(time: string | null) {
  return time ? time.slice(0, 5) : null
}

/** True when the coupon is not restricted to a part of the day. */
export function isWholeDay(coupon: Pick<MyCoupon, 'startTime' | 'endTime'>) {
  const start = toShortTime(coupon.startTime)
  const end = toShortTime(coupon.endTime)
  return (!start || start === '00:00') && (!end || end === '23:59')
}

/** Expiry further out than this reads as "forever" to users, so it isn't shown. */
const COUPON_END_DATE_VISIBLE_DAYS = 60

/** Whether a coupon card should show its "valid until" date: only when the
 * coupon expires within the next 60 days. */
export function shouldShowCouponEndDate(coupon: Pick<MyCoupon, 'endDate'>, now: Date = new Date()) {
  return !isAfter(new Date(coupon.endDate), addDays(now, COUPON_END_DATE_VISIBLE_DAYS))
}

export type CouponShopCoverage =
  | { kind: 'all' }
  | { kind: 'one'; shop: MyCoupon['shops'][number] }
  | { kind: 'some'; count: number; total: number }
  | { kind: 'none' }

/** Where in the current country the coupon is redeemable: every active shop,
 * a single one, a subset, or none (e.g. a coupon for another country). */
export function getCouponShopCoverage(coupon: Pick<MyCoupon, 'shops' | 'regionShopCount'>): CouponShopCoverage {
  const count = coupon.shops.length
  return count === 0
    ? { kind: 'none' }
    : count >= coupon.regionShopCount
      ? { kind: 'all' }
      : count === 1
        ? { kind: 'one', shop: coupon.shops[0] }
        : { kind: 'some', count, total: coupon.regionShopCount }
}
